import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Plus, Search, Edit, Trash2, Phone, Mail, MapPin, 
  FileText, Building2, User, Power, PowerOff
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [supplierTransactions, setSupplierTransactions] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    nit: '',
    address: '',
    phone: '',
    email: '',
    contact_person: '',
    notes: ''
  });

  useEffect(() => {
    fetchSuppliers();
  }, [showInactive]);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/suppliers?active_only=${!showInactive}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Error al cargar proveedores');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.nit) {
      toast.error('Nombre y NIT son obligatorios');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      if (editMode && selectedSupplier) {
        await axios.put(`${API}/suppliers/${selectedSupplier.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Proveedor actualizado');
      } else {
        await axios.post(`${API}/suppliers`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Proveedor creado exitosamente');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar proveedor');
    }
  };

  const handleEdit = (supplier) => {
    setFormData({
      name: supplier.name,
      nit: supplier.nit,
      address: supplier.address || '',
      phone: supplier.phone || '',
      email: supplier.email || '',
      contact_person: supplier.contact_person || '',
      notes: supplier.notes || ''
    });
    setSelectedSupplier(supplier);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedSupplier) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/suppliers/${selectedSupplier.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setDeleteDialogOpen(false);
      setSelectedSupplier(null);
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar proveedor');
    }
  };

  const handleViewTransactions = async (supplier) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/suppliers/${supplier.id}/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSupplierTransactions(response.data);
      setSelectedSupplier(supplier);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Error al cargar transacciones');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      nit: '',
      address: '',
      phone: '',
      email: '',
      contact_person: '',
      notes: ''
    });
    setSelectedSupplier(null);
    setEditMode(false);
  };

  const filteredSuppliers = suppliers.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.nit.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (value) => {
    return `Q${(value || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-GT', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="suppliers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Building2 className="h-8 w-8 text-indigo-600" />
            Proveedores
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Gestiona el catalogo de proveedores
          </p>
        </div>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="new-supplier-button">
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Proveedor
        </Button>
      </div>

      {/* Tabla de Proveedores */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <CardTitle className="text-lg">Listado de Proveedores</CardTitle>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <div className="relative flex-1 sm:w-72">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Buscar por nombre o NIT..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-supplier-input"
                />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="rounded border-slate-300"
                />
                <span className="text-slate-600 dark:text-slate-400">Mostrar inactivos</span>
              </label>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="suppliers-table">
              <thead>
                <tr className="border-y border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                  <th className="text-left py-3 px-4 font-semibold text-slate-700 dark:text-slate-300 text-sm">Nombre</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700 dark:text-slate-300 text-sm">NIT</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700 dark:text-slate-300 text-sm hidden md:table-cell">Telefono</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700 dark:text-slate-300 text-sm hidden lg:table-cell">Ubicacion</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700 dark:text-slate-300 text-sm hidden lg:table-cell">Contacto</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700 dark:text-slate-300 text-sm">Estado</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700 dark:text-slate-300 text-sm">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredSuppliers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="py-12 text-center">
                      <Building2 className="h-12 w-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                      <p className="text-slate-500 dark:text-slate-400">
                        {searchTerm ? 'No se encontraron proveedores' : 'No hay proveedores registrados'}
                      </p>
                      {!searchTerm && (
                        <Button 
                          variant="outline" 
                          className="mt-3"
                          onClick={() => { resetForm(); setDialogOpen(true); }}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Agregar Proveedor
                        </Button>
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredSuppliers.map(supplier => (
                    <tr 
                      key={supplier.id} 
                      className={`border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors ${
                        !supplier.is_active ? 'opacity-50' : ''
                      }`}
                      data-testid={`supplier-row-${supplier.id}`}
                    >
                      <td className="py-3 px-4">
                        <div className="font-medium text-slate-900 dark:text-white">
                          {supplier.name}
                        </div>
                        {supplier.email && (
                          <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-0.5">
                            <Mail className="h-3 w-3" />
                            {supplier.email}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-mono text-sm text-indigo-600 dark:text-indigo-400">
                          {supplier.nit}
                        </span>
                      </td>
                      <td className="py-3 px-4 hidden md:table-cell">
                        {supplier.phone ? (
                          <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400">
                            <Phone className="h-3.5 w-3.5" />
                            {supplier.phone}
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 hidden lg:table-cell">
                        {supplier.address ? (
                          <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400 max-w-[200px] truncate" title={supplier.address}>
                            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                            {supplier.address}
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 hidden lg:table-cell">
                        {supplier.contact_person ? (
                          <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400">
                            <User className="h-3.5 w-3.5" />
                            {supplier.contact_person}
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          supplier.is_active
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                          {supplier.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => handleViewTransactions(supplier)}
                            title="Ver historial"
                          >
                            <FileText className="h-4 w-4 text-slate-600" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => handleEdit(supplier)}
                            title="Editar"
                          >
                            <Edit className="h-4 w-4 text-slate-600" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                            onClick={() => {
                              setSelectedSupplier(supplier);
                              setDeleteDialogOpen(true);
                            }}
                            title="Eliminar"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Footer con total */}
          {filteredSuppliers.length > 0 && (
            <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Mostrando <span className="font-semibold text-slate-900 dark:text-white">{filteredSuppliers.length}</span> proveedor{filteredSuppliers.length !== 1 ? 'es' : ''}
                {searchTerm && ` de ${suppliers.length} total`}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Crear/Editar */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-indigo-600" />
              {editMode ? 'Editar Proveedor' : 'Nuevo Proveedor'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre del Proveedor *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Coca Cola"
                  required
                  data-testid="supplier-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>NIT *</Label>
                <Input
                  value={formData.nit}
                  onChange={(e) => setFormData({ ...formData, nit: e.target.value })}
                  placeholder="12345678-9"
                  required
                  data-testid="supplier-nit-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Direccion</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Direccion del proveedor"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Telefono</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="2222-3333"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Correo Electronico</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="contacto@proveedor.com"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Persona de Contacto</Label>
              <Input
                value={formData.contact_person}
                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                placeholder="Nombre del contacto"
              />
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Observaciones adicionales"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                Cancelar
              </Button>
              <Button type="submit" className="flex-1" data-testid="save-supplier-button">
                {editMode ? 'Guardar Cambios' : 'Crear Proveedor'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Historial de Transacciones */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-indigo-600" />
              Historial - {selectedSupplier?.name}
            </DialogTitle>
          </DialogHeader>
          
          {supplierTransactions && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Total Compras</p>
                  <p className="text-xl font-bold text-slate-900 dark:text-white">
                    {supplierTransactions.total_transactions || 0}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Monto Total</p>
                  <p className="text-xl font-bold text-indigo-600 dark:text-indigo-400">
                    {formatCurrency(supplierTransactions.total_amount)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">NIT</p>
                  <p className="text-xl font-bold text-slate-900 dark:text-white font-mono">
                    {selectedSupplier?.nit}
                  </p>
                </div>
              </div>

              {supplierTransactions.transactions?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 dark:bg-slate-800">
                      <tr>
                        <th className="text-left p-3 font-semibold">Fecha</th>
                        <th className="text-left p-3 font-semibold">Descripcion</th>
                        <th className="text-left p-3 font-semibold">Factura</th>
                        <th className="text-right p-3 font-semibold">Monto</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {supplierTransactions.transactions.map(tx => (
                        <tr key={tx.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                          <td className="p-3">{formatDate(tx.transaction_date)}</td>
                          <td className="p-3">{tx.description}</td>
                          <td className="p-3 font-mono text-slate-600">{tx.invoice_number || '-'}</td>
                          <td className="p-3 text-right font-mono font-semibold">
                            {formatCurrency(tx.total_amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  No hay transacciones registradas con este proveedor
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Confirmar Eliminación */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Eliminar Proveedor
            </DialogTitle>
          </DialogHeader>
          
          <p className="text-slate-600 dark:text-slate-400">
            ¿Estas seguro de eliminar al proveedor <strong>{selectedSupplier?.name}</strong>?
          </p>
          <p className="text-sm text-slate-500">
            Si tiene transacciones asociadas, sera desactivado en lugar de eliminado.
          </p>

          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="flex-1">
              Cancelar
            </Button>
            <Button 
              onClick={handleDelete} 
              className="flex-1 bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-button"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Eliminar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Suppliers;
